/*
 * Copyright (c) Nexign, JSC, 2019-2022.
 */

/**
 * The main component for render data by Charts
 *
 * @author Yakov Gushchin
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.21.0
 * @since 1.1.0
 */

import { cloneElement, FC, Fragment, useEffect, useState } from 'react';

import maxBy from 'lodash/maxBy';

import { withStatuses } from '../../../modules/app/hoc/WithLoading';
import { IPageExpensesAnalyzerTranslation } from '../../../modules/internationalization/types';
import { Loader } from '../../../modules/loader/components/Loader';
import BizCharts from './BizchartsLoadable';
import ChartError from './ChartError';
import ChartLegend from './ChartLegend/ChartLegend';
import ChartLoading from './ChartLoading';
import { getChartImplementation } from './ChartsImplementation/chartImplementation';
import { EChartImplementationType, IChartImplementation } from './ChartsImplementation/chartImplementationTypes';
import {
    EChartDataKeyEnum,
    IChartColumnWidthConfiguration,
    IChartDataByAccount,
    IChartDataInput,
    IChartDataOutput,
    IChartLabels,
    IChartTooltipItem,
    TMapNameToColor,
    TMapNameToTranslation,
} from './chartTypes';

export interface IChartProps {
    chartRenderCallback: (state: boolean) => void;
    currency: string;
    data: IChartDataInput[];
    date: string;
    isEmpty: boolean;
    isError: boolean;
    isHideTooltipDate?: boolean;
    isLoading: boolean;
    mapNameToColor: TMapNameToColor;
    mapIdToLabel: IChartLabels;
    mapNameToTranslation: TMapNameToTranslation;
    onLegendClickHandler: (chartItem: IChartDataInput) => void;
    translation?: IPageExpensesAnalyzerTranslation;
    type: EChartImplementationType;
    width: number;
}

export const Chart: FC<IChartProps> = props => {

    const [chart, setChart] = useState<G2.Chart>();
    const [realData, setRealData] = useState<IChartDataByAccount>({});

    /*
    * -------------------------------------------
    *           IMPLEMENTATION
    * -------------------------------------------
    */

    const chartImplementation: IChartImplementation = getChartImplementation(props.type);
    const scale = chartImplementation.scale;

    /*
    * -------------------------------------------
    *           DATA CONFIGURATION
    * -------------------------------------------
    */



    useEffect(() => {
        setTimeout(() => {
            configureData();
        });
    }, [chart, props.data]);

    useEffect(() => {
        props.chartRenderCallback(true);
        return () => {
            props.chartRenderCallback(false);
        };
    });

    const configureData = (): void => {
        const reducedData: IChartDataOutput[] = [];
        const newRealData: IChartDataByAccount = {};
        const activeItems: IChartDataInput[] = props.data.filter((c: IChartDataInput) => c[EChartDataKeyEnum.checked]);
        const totalCount = maxBy(activeItems, EChartDataKeyEnum.total)?.[EChartDataKeyEnum.total];

        activeItems
            .forEach((item: IChartDataInput) => Object
                .keys(item[EChartDataKeyEnum.y])
                .forEach((account: string) => {
                        if (props.type === EChartImplementationType.category) {
                            if (!newRealData[account]) {
                                newRealData[account] = {};
                            }
                            newRealData[account][item[EChartDataKeyEnum.x]] = item[EChartDataKeyEnum.y][account]
                        }

                        reducedData.push(chartImplementation.dataConfiguration(item, account, totalCount || 0));
                    },
                ),
            );

        setRealData(newRealData);

        /**
         * Note: Vendor's chart is show a error sometimes because 'plot' is not ready
         */
        // @ts-ignore
        chart?.get('plot') && chart.changeData(reducedData);
    };

    /*
    * -------------------------------------------
    *           CHART
    * -------------------------------------------
    */

    const onGetG2Instance = (chart: G2.Chart): void => {
        setChart(chart);
    };

    const onMouseLeaveHandler = (): void => {
        chart?.hideTooltip();
    };

    /*
    * -------------------------------------------
    *           GEOM
    * -------------------------------------------
    */

    const setGeomColor = (name: string): string => props.mapNameToColor[name];

    const setGeomSize = (columnWidthConfiguration: IChartColumnWidthConfiguration): number | undefined => {
        const geomColumns = new Set();
        props.data
            .filter((item: IChartDataInput) => item[EChartDataKeyEnum.checked])
            .forEach((item: IChartDataInput) => Object
                .keys(item[EChartDataKeyEnum.y])
                .forEach((account: string) => {
                    geomColumns.add(account);
                }),
            );
        if (geomColumns.size < columnWidthConfiguration.columnsCount) {
            return columnWidthConfiguration.maxWidth;
        } else {
            return undefined;
        }
    };

    /*
    * -------------------------------------------
    *           TOOLTIP
    * -------------------------------------------
    */

    const templateTooltip = (title?: string, items?: IChartTooltipItem[]): string => {
        if (items && title) {
            let values: { [field: string]: number } = {};
            if (props.type === EChartImplementationType.category) {
                const account = items[0].title;
                values = realData[account]
            }

            let total = 0;
            const filteredItems = items
                .filter((item: IChartTooltipItem) => item.value > 0 || chartImplementation.tooltip.isNotFiltered)
                .map((item: IChartTooltipItem) => {
                    let value = item.value;
                    if (props.type === EChartImplementationType.category) {
                        value = values[item.name];
                    }

                    item.color = props.mapNameToColor[item.name];
                    item.id = item.name;
                    item.name = props.mapNameToTranslation[item.name];
                    item.value = +value;
                    total += value;
                    return item;
                })
                .reverse()
            ;
            return chartImplementation.tooltip.render({
                account: title,
                currency: props.currency,
                date: props.date,
                isHideTooltipDate: props.isHideTooltipDate,
                items: filteredItems,
                mapIdToLabel: props.mapIdToLabel,
                title: 'Всего',
                total,
            });
        }

        return '<div></div>';
    };

    return (
        <Fragment>
            {
                cloneElement(chartImplementation.render(), {}, (
                    <Fragment>
                        <BizCharts fallback={<Loader showLoader={true}/>}>
                            {
                                ({ Chart, Axis, Tooltip, Geom }) => (
                                    <Fragment>
                                        <ChartLegend
                                            chartType={props.type}
                                            className={chartImplementation.legend.className}
                                            dataName={chartImplementation.legend.dataName}
                                            items={props.data}
                                            legendRow={chartImplementation.legend.legendRow}
                                            mapIdToLabel={props.mapIdToLabel}
                                            mapNameToColor={props.mapNameToColor}
                                            mapNameToTranslation={props.mapNameToTranslation}
                                            withSubtitle={chartImplementation.legend.withSubtitle}
                                            onClick={props.onLegendClickHandler}
                                        />

                                        <div data-component="chartGraph" onMouseLeave={onMouseLeaveHandler}>
                                            <Chart
                                                height={chartImplementation.height}
                                                padding={chartImplementation.padding}
                                                scale={scale}
                                                width={props.width}
                                                onGetG2Instance={onGetG2Instance}
                                            >
                                                <Axis
                                                    grid={chartImplementation.grid.y}
                                                    name={EChartDataKeyEnum.y}
                                                />

                                                <Axis
                                                    grid={chartImplementation.grid.x}
                                                    label={({ htmlTemplate: () => '' })}
                                                    name={EChartDataKeyEnum.x}
                                                />
                                                <Tooltip
                                                    useHtml
                                                    crosshairs={chartImplementation.tooltip.crosshair}
                                                    htmlContent={templateTooltip}
                                                />

                                                <Geom
                                                    animate={chartImplementation.animate}
                                                    color={[EChartDataKeyEnum.name, setGeomColor]}
                                                    opacity={1}
                                                    position={`${EChartDataKeyEnum.x}*${EChartDataKeyEnum.y}`}
                                                    size={chartImplementation.columnWidth ?
                                                        setGeomSize(chartImplementation.columnWidth) : undefined}
                                                    type={chartImplementation.type}
                                                />
                                            </Chart>
                                        </div>
                                        {props.children}
                                    </Fragment>
                                )
                            }
                        </BizCharts>
                    </Fragment>
                ))
            }
        </Fragment>
    );
};

export default withStatuses<IChartProps, IChartProps, IChartProps>(ChartLoading)(ChartError)(ChartError)(Chart);
